import { useEffect, useMemo, useRef, useState } from 'react';
import { SOURCE_RECORDING_PROFILE } from '../video/sourceRecordingProfile';

type FitMode = 'contain' | 'cover';
type RunStatus = 'idle' | 'recording' | 'complete' | 'failed';

interface CompressionProfile {
  id: string;
  label: string;
  width: number;
  height: number;
  fps: number;
  bitrateMbps: number;
  note: string;
}

interface CompressionResult {
  id: string;
  profile: CompressionProfile;
  status: RunStatus;
  startedAt: number;
  durationSeconds: number;
  bytes: number;
  actualBitrateMbps: number;
  mimeType: string;
  url: string | null;
  error: string | null;
}

const PROFILES: CompressionProfile[] = [
  {
    id: '1080-balanced',
    label: '1080p balanced',
    width: 1920,
    height: 1080,
    fps: 8,
    bitrateMbps: 8,
    note: 'Smallest practical reference video control.',
  },
  {
    id: '1440-light',
    label: '1440p light',
    width: 2560,
    height: 1440,
    fps: 5,
    bitrateMbps: 6,
    note: 'Long-pose lower bound.',
  },
  {
    id: '1440-balanced',
    label: '1440p balanced',
    width: 2560,
    height: 1440,
    fps: 8,
    bitrateMbps: 10,
    note: 'Likely first candidate.',
  },
  {
    id: '1440-quality',
    label: SOURCE_RECORDING_PROFILE.label,
    width: SOURCE_RECORDING_PROFILE.width,
    height: SOURCE_RECORDING_PROFILE.height,
    fps: SOURCE_RECORDING_PROFILE.frameRate,
    bitrateMbps: SOURCE_RECORDING_PROFILE.videoBitsPerSecond / 1_000_000,
    note: 'Production source recording target.',
  },
  {
    id: '1440-high',
    label: '1440p high',
    width: 2560,
    height: 1440,
    fps: 12,
    bitrateMbps: 18,
    note: 'Upper 1440p quality reference.',
  },
  {
    id: '4k-control',
    label: '4K control',
    width: 3840,
    height: 2160,
    fps: 12,
    bitrateMbps: 30,
    note: 'Large-file quality comparison.',
  },
];

const SOURCE_RESOLUTIONS = {
  '1080p': { width: 1920, height: 1080 },
  '1440p': { width: 2560, height: 1440 },
  '4k': { width: 3840, height: 2160 },
};

function getSupportedMimeType(includeAudio: boolean) {
  if (typeof MediaRecorder === 'undefined') return null;

  const candidates = includeAudio
    ? [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4;codecs=h264,aac',
      'video/mp4',
    ]
    : [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4;codecs=h264',
      'video/mp4',
    ];

  return candidates.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ?? '';
}

function formatFileSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatProjectedSize(bytes: number, durationSeconds: number, targetSeconds: number) {
  if (!bytes || durationSeconds <= 0) return '0 KB';
  return formatFileSize((bytes / durationSeconds) * targetSeconds);
}

function formatBitrate(value: number) {
  if (!Number.isFinite(value) || value <= 0) return '0 Mbps';
  return `${value.toFixed(value >= 10 ? 1 : 2)} Mbps`;
}

function getPixelBudget(result: CompressionResult) {
  const { profile, actualBitrateMbps } = result;
  if (!actualBitrateMbps) return 0;
  return (actualBitrateMbps * 1_000_000) / (profile.width * profile.height * profile.fps);
}

function getBudgetLabel(pixelBudget: number) {
  if (pixelBudget >= 0.32) return 'Generous';
  if (pixelBudget >= 0.22) return 'Good';
  if (pixelBudget >= 0.14) return 'Tight';
  return 'Risky';
}

function drawVideoFrame(
  context: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  width: number,
  height: number,
  fitMode: FitMode
) {
  const videoWidth = video.videoWidth || width;
  const videoHeight = video.videoHeight || height;
  const scale = fitMode === 'cover'
    ? Math.max(width / videoWidth, height / videoHeight)
    : Math.min(width / videoWidth, height / videoHeight);
  const drawWidth = videoWidth * scale;
  const drawHeight = videoHeight * scale;
  const x = (width - drawWidth) / 2;
  const y = (height - drawHeight) / 2;

  context.fillStyle = '#111';
  context.fillRect(0, 0, width, height);
  context.drawImage(video, x, y, drawWidth, drawHeight);
}

function stopStream(stream: MediaStream | null) {
  stream?.getTracks().forEach((track) => track.stop());
}

export function RecordingCompressionTestPage() {
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const resultsRef = useRef<CompressionResult[]>([]);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [sourceResolution, setSourceResolution] = useState<keyof typeof SOURCE_RESOLUTIONS>('4k');
  const [durationSeconds, setDurationSeconds] = useState(12);
  const [includeAudio, setIncludeAudio] = useState(false);
  const [fitMode, setFitMode] = useState<FitMode>('contain');
  const [selectedProfileIds, setSelectedProfileIds] = useState<Set<string>>(
    () => new Set(['1440-balanced', '1440-quality', '4k-control'])
  );
  const [results, setResults] = useState<CompressionResult[]>([]);
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [runMessage, setRunMessage] = useState<string | null>(null);

  const supportedMimeType = useMemo(() => getSupportedMimeType(includeAudio), [includeAudio]);
  const canRecord = Boolean(cameraStream && supportedMimeType !== null && !activeProfileId);

  useEffect(() => {
    if (!previewVideoRef.current) return;
    previewVideoRef.current.srcObject = cameraStream;
  }, [cameraStream]);

  useEffect(() => {
    resultsRef.current = results;
  }, [results]);

  useEffect(() => {
    return () => {
      stopStream(streamRef.current);
      resultsRef.current.forEach((result) => {
        if (result.url) URL.revokeObjectURL(result.url);
      });
    };
  }, []);

  const refreshDevices = async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const nextDevices = await navigator.mediaDevices.enumerateDevices();
    setDevices(nextDevices.filter((device) => device.kind === 'videoinput'));
  };

  const startCamera = async () => {
    setCameraError(null);
    setRunMessage(null);

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Camera capture is not available in this browser.');
      }

      stopStream(streamRef.current);
      const source = SOURCE_RESOLUTIONS[sourceResolution];
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
          width: { ideal: source.width },
          height: { ideal: source.height },
          frameRate: { ideal: 30 },
        },
        audio: includeAudio,
      });

      streamRef.current = stream;
      setCameraStream(stream);
      await refreshDevices();
      const trackSettings = stream.getVideoTracks()[0]?.getSettings();
      if (trackSettings?.deviceId) {
        setSelectedDeviceId(trackSettings.deviceId);
      }
    } catch (error) {
      setCameraStream(null);
      streamRef.current = null;
      setCameraError(error instanceof Error ? error.message : 'Could not start the camera.');
    }
  };

  const stopCamera = () => {
    stopStream(streamRef.current);
    streamRef.current = null;
    setCameraStream(null);
    setActiveProfileId(null);
  };

  const toggleProfile = (profileId: string) => {
    setSelectedProfileIds((current) => {
      const next = new Set(current);
      if (next.has(profileId)) {
        next.delete(profileId);
      } else {
        next.add(profileId);
      }
      return next;
    });
  };

  const resetResults = () => {
    results.forEach((result) => {
      if (result.url) URL.revokeObjectURL(result.url);
    });
    setResults([]);
  };

  const recordProfile = async (profile: CompressionProfile): Promise<CompressionResult> => {
    const video = previewVideoRef.current;
    const canvas = canvasRef.current;
    const sourceStream = streamRef.current;

    if (!video || !canvas || !sourceStream) {
      throw new Error('Start the camera before recording a sample.');
    }
    if (!canvas.captureStream) {
      throw new Error('Canvas recording is not available in this browser.');
    }
    if (supportedMimeType === null) {
      throw new Error('MediaRecorder is not available in this browser.');
    }

    canvas.width = profile.width;
    canvas.height = profile.height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('Could not prepare the recording canvas.');

    drawVideoFrame(context, video, profile.width, profile.height, fitMode);
    const frameIntervalMs = 1000 / profile.fps;
    const frameTimer = window.setInterval(() => {
      drawVideoFrame(context, video, profile.width, profile.height, fitMode);
    }, frameIntervalMs);

    const canvasStream = canvas.captureStream(profile.fps);
    const recordingTracks = [
      ...canvasStream.getVideoTracks(),
      ...(includeAudio ? sourceStream.getAudioTracks() : []),
    ];
    const recordingStream = new MediaStream(recordingTracks);
    const chunks: Blob[] = [];
    const startedAt = performance.now();

    const recorder = new MediaRecorder(recordingStream, {
      ...(supportedMimeType ? { mimeType: supportedMimeType } : {}),
      videoBitsPerSecond: profile.bitrateMbps * 1_000_000,
      ...(includeAudio ? { audioBitsPerSecond: 128_000 } : {}),
    });

    return new Promise<CompressionResult>((resolve, reject) => {
      let settled = false;

      const cleanUp = () => {
        window.clearInterval(frameTimer);
        canvasStream.getTracks().forEach((track) => track.stop());
      };

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onerror = () => {
        if (settled) return;
        settled = true;
        cleanUp();
        reject(new Error(`Recording failed for ${profile.label}.`));
      };

      recorder.onstop = () => {
        if (settled) return;
        settled = true;
        cleanUp();

        const endedAt = performance.now();
        const actualDurationSeconds = Math.max(0.1, (endedAt - startedAt) / 1000);
        const blob = new Blob(chunks, { type: recorder.mimeType || supportedMimeType || 'video/webm' });
        const url = URL.createObjectURL(blob);
        resolve({
          id: `${profile.id}-${Date.now()}`,
          profile,
          status: 'complete',
          startedAt: Date.now(),
          durationSeconds: actualDurationSeconds,
          bytes: blob.size,
          actualBitrateMbps: (blob.size * 8) / actualDurationSeconds / 1_000_000,
          mimeType: blob.type || recorder.mimeType || 'video/webm',
          url,
          error: null,
        });
      };

      recorder.start(1000);
      window.setTimeout(() => {
        if (recorder.state !== 'inactive') {
          recorder.requestData();
          recorder.stop();
        }
      }, Math.max(2, durationSeconds) * 1000);
    });
  };

  const runProfiles = async (profiles: CompressionProfile[]) => {
    if (profiles.length === 0) {
      setRunMessage('Select at least one profile.');
      return;
    }

    setRunMessage(null);

    for (const profile of profiles) {
      setActiveProfileId(profile.id);
      setResults((current) => [
        {
          id: `${profile.id}-running`,
          profile,
          status: 'recording',
          startedAt: Date.now(),
          durationSeconds: 0,
          bytes: 0,
          actualBitrateMbps: 0,
          mimeType: supportedMimeType || 'browser default',
          url: null,
          error: null,
        },
        ...current.filter((result) => result.id !== `${profile.id}-running`),
      ]);

      try {
        const result = await recordProfile(profile);
        setResults((current) => [
          result,
          ...current.filter((item) => item.id !== `${profile.id}-running`),
        ]);
      } catch (error) {
        setResults((current) => [
          {
            id: `${profile.id}-${Date.now()}`,
            profile,
            status: 'failed',
            startedAt: Date.now(),
            durationSeconds: 0,
            bytes: 0,
            actualBitrateMbps: 0,
            mimeType: supportedMimeType || 'browser default',
            url: null,
            error: error instanceof Error ? error.message : 'Recording failed.',
          },
          ...current.filter((item) => item.id !== `${profile.id}-running`),
        ]);
      }
    }

    setActiveProfileId(null);
  };

  const selectedProfiles = PROFILES.filter((profile) => selectedProfileIds.has(profile.id));
  const sourceSettings = cameraStream?.getVideoTracks()[0]?.getSettings();

  return (
    <main className="min-h-screen bg-bg text-text">
      <div className="mx-auto max-w-[1500px] px-5 py-6 lg:px-8">
        <header className="mb-6 flex flex-col gap-4 border-b border-line pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium text-text-dim">Internal recording lab</p>
            <h1 className="font-display text-4xl font-light leading-tight text-text md:text-6xl">
              Recording compression test
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn" onClick={() => window.location.assign('/test')}>
              Test harness
            </button>
            <button type="button" className="btn" onClick={resetResults} disabled={activeProfileId !== null}>
              Clear results
            </button>
          </div>
        </header>

        <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
          <div className="space-y-5">
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1.25fr)_minmax(360px,0.75fr)]">
              <section className="overflow-hidden rounded-lg border border-line bg-panel">
                <div className="aspect-video bg-black">
                  <video
                    ref={previewVideoRef}
                    className="h-full w-full object-contain"
                    muted
                    playsInline
                    autoPlay
                  />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line p-4 text-sm text-text-dim">
                  <span>
                    {sourceSettings?.width && sourceSettings?.height
                      ? `${sourceSettings.width} x ${sourceSettings.height}`
                      : 'Camera not started'}
                    {sourceSettings?.frameRate ? ` · ${Math.round(sourceSettings.frameRate)} fps source` : ''}
                  </span>
                  <span>{supportedMimeType === null ? 'MediaRecorder unavailable' : supportedMimeType || 'Browser default encoder'}</span>
                </div>
              </section>

              <section className="rounded-lg border border-line bg-panel p-4">
                <h2 className="mb-4 text-lg font-medium">Capture setup</h2>
                <div className="space-y-4">
                  <label className="block">
                    <span className="text-sm text-text-dim">Camera</span>
                    <select
                      className="input mt-1 h-11 text-sm"
                      value={selectedDeviceId}
                      onChange={(event) => setSelectedDeviceId(event.target.value)}
                    >
                      <option value="">Default camera</option>
                      {devices.map((device, index) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label || `Camera ${index + 1}`}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-sm text-text-dim">Requested source</span>
                      <select
                        className="input mt-1 h-11 text-sm"
                        value={sourceResolution}
                        onChange={(event) => setSourceResolution(event.target.value as keyof typeof SOURCE_RESOLUTIONS)}
                      >
                        <option value="1080p">1080p</option>
                        <option value="1440p">1440p</option>
                        <option value="4k">4K</option>
                      </select>
                    </label>

                    <label className="block">
                      <span className="text-sm text-text-dim">Sample length</span>
                      <input
                        className="input mt-1 h-11 text-sm"
                        type="number"
                        min="2"
                        max="90"
                        value={durationSeconds}
                        onChange={(event) => setDurationSeconds(Math.max(2, Number(event.target.value) || 2))}
                      />
                    </label>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <span className="text-sm text-text-dim">Framing</span>
                      <select
                        className="input mt-1 h-11 text-sm"
                        value={fitMode}
                        onChange={(event) => setFitMode(event.target.value as FitMode)}
                      >
                        <option value="contain">Contain</option>
                        <option value="cover">Cover</option>
                      </select>
                    </label>

                    <label className="flex items-center gap-3 rounded-lg border border-line-soft bg-bg-secondary px-3 py-3 text-sm font-medium">
                      <input
                        type="checkbox"
                        checked={includeAudio}
                        onChange={(event) => setIncludeAudio(event.target.checked)}
                      />
                      Mic audio
                    </label>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="btn btn-accent" onClick={startCamera} disabled={activeProfileId !== null}>
                      {cameraStream ? 'Restart camera' : 'Start camera'}
                    </button>
                    <button type="button" className="btn" onClick={stopCamera} disabled={!cameraStream || activeProfileId !== null}>
                      Stop camera
                    </button>
                  </div>

                  {cameraError && <p className="text-sm text-warn">{cameraError}</p>}
                  {runMessage && <p className="text-sm text-warn">{runMessage}</p>}
                </div>
              </section>
            </div>

            <section className="rounded-lg border border-line bg-panel p-4">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-lg font-medium">Profile matrix</h2>
                  <p className="text-sm text-text-dim">
                    Selected profiles run one after another from the same camera scene.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setSelectedProfileIds(new Set(PROFILES.map((profile) => profile.id)))}
                    disabled={activeProfileId !== null}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setSelectedProfileIds(new Set())}
                    disabled={activeProfileId !== null}
                  >
                    Select none
                  </button>
                  <button
                    type="button"
                    className="btn btn-accent"
                    onClick={() => runProfiles(selectedProfiles)}
                    disabled={!canRecord || selectedProfiles.length === 0}
                  >
                    {activeProfileId ? 'Recording...' : `Run ${selectedProfiles.length} selected`}
                  </button>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {PROFILES.map((profile) => {
                  const selected = selectedProfileIds.has(profile.id);
                  const active = activeProfileId === profile.id;
                  return (
                    <article
                      key={profile.id}
                      className={`rounded-lg border p-4 transition-colors ${
                        selected ? 'border-text bg-bg-secondary' : 'border-line bg-panel'
                      }`}
                    >
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <label className="flex items-center gap-2 text-sm font-medium">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleProfile(profile.id)}
                            disabled={activeProfileId !== null}
                          />
                          {profile.label}
                        </label>
                        {active && <span className="rounded bg-text px-2 py-1 text-[11px] text-white">Recording</span>}
                      </div>
                      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                        <div>
                          <dt className="text-text-faint">Resolution</dt>
                          <dd>{profile.width} x {profile.height}</dd>
                        </div>
                        <div>
                          <dt className="text-text-faint">FPS</dt>
                          <dd>{profile.fps}</dd>
                        </div>
                        <div>
                          <dt className="text-text-faint">Target</dt>
                          <dd>{profile.bitrateMbps} Mbps</dd>
                        </div>
                        <div>
                          <dt className="text-text-faint">45 min est.</dt>
                          <dd>{formatFileSize((profile.bitrateMbps * 1_000_000 / 8) * 45 * 60)}</dd>
                        </div>
                      </dl>
                      <p className="mt-3 text-xs leading-relaxed text-text-dim">{profile.note}</p>
                      <button
                        type="button"
                        className="btn btn-sm mt-4 w-full"
                        onClick={() => runProfiles([profile])}
                        disabled={!canRecord}
                      >
                        Record sample
                      </button>
                    </article>
                  );
                })}
              </div>
            </section>
          </div>

          <aside className="space-y-5">
            <section className="rounded-lg border border-line bg-panel p-4">
              <h2 className="mb-3 text-lg font-medium">Run notes</h2>
              <div className="space-y-3 text-sm leading-relaxed text-text-dim">
                <p>Actual bitrate can diverge from target bitrate because browser encoders make their own choices.</p>
                <p>The pixel budget label is a rough bits-per-pixel-per-frame signal, useful for comparing rows from the same browser.</p>
                <p>Use the same camera scene for every profile. Hair, fabric, hands, and low-light noise are where compression problems usually show first.</p>
              </div>
            </section>

            <section className="rounded-lg border border-line bg-panel p-4">
              <h2 className="mb-3 text-lg font-medium">Quick targets</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-text-dim">45 min at 10 Mbps</span>
                  <span>{formatFileSize((10_000_000 / 8) * 45 * 60)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-text-dim">45 min at 14 Mbps</span>
                  <span>{formatFileSize((14_000_000 / 8) * 45 * 60)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-text-dim">2 hr at 10 Mbps</span>
                  <span>{formatFileSize((10_000_000 / 8) * 2 * 60 * 60)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-text-dim">2 hr at 14 Mbps</span>
                  <span>{formatFileSize((14_000_000 / 8) * 2 * 60 * 60)}</span>
                </div>
              </div>
            </section>
          </aside>
        </section>

        <section className="mt-5 rounded-lg border border-line bg-panel p-4">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-lg font-medium">Results</h2>
              <p className="text-sm text-text-dim">Projected sizes are based on the actual bytes produced by this browser sample.</p>
            </div>
            <span className="text-sm text-text-dim">{results.length} samples</span>
          </div>

          {results.length === 0 ? (
            <div className="rounded-lg border border-dashed border-line p-8 text-center text-text-dim">
              Start the camera and record one or more profiles.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-left text-sm">
                <thead className="border-b border-line text-xs uppercase text-text-faint">
                  <tr>
                    <th className="py-3 pr-3 font-medium">Profile</th>
                    <th className="py-3 pr-3 font-medium">Actual size</th>
                    <th className="py-3 pr-3 font-medium">Actual bitrate</th>
                    <th className="py-3 pr-3 font-medium">45 min</th>
                    <th className="py-3 pr-3 font-medium">2 hours</th>
                    <th className="py-3 pr-3 font-medium">Budget</th>
                    <th className="py-3 pr-3 font-medium">Playback</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line-soft">
                  {results.map((result) => {
                    const budget = getPixelBudget(result);
                    return (
                      <tr key={result.id}>
                        <td className="py-4 pr-3 align-top">
                          <div className="font-medium">{result.profile.label}</div>
                          <div className="text-xs text-text-dim">
                            {result.profile.width} x {result.profile.height} · {result.profile.fps} fps · target {result.profile.bitrateMbps} Mbps
                          </div>
                          {result.status === 'failed' && <div className="mt-1 text-xs text-warn">{result.error}</div>}
                          {result.status === 'recording' && <div className="mt-1 text-xs text-text-dim">Recording sample...</div>}
                        </td>
                        <td className="py-4 pr-3 align-top">{formatFileSize(result.bytes)}</td>
                        <td className="py-4 pr-3 align-top">
                          <div>{formatBitrate(result.actualBitrateMbps)}</div>
                          <div className="text-xs text-text-dim">{result.mimeType}</div>
                        </td>
                        <td className="py-4 pr-3 align-top">{formatProjectedSize(result.bytes, result.durationSeconds, 45 * 60)}</td>
                        <td className="py-4 pr-3 align-top">{formatProjectedSize(result.bytes, result.durationSeconds, 2 * 60 * 60)}</td>
                        <td className="py-4 pr-3 align-top">
                          {result.status === 'complete' ? (
                            <>
                              <div>{getBudgetLabel(budget)}</div>
                              <div className="text-xs text-text-dim">{budget.toFixed(3)} bpp/f</div>
                            </>
                          ) : (
                            <span className="text-text-dim">-</span>
                          )}
                        </td>
                        <td className="py-4 pr-3 align-top">
                          {result.url ? (
                            <div className="w-56 space-y-2">
                              <video src={result.url} controls className="aspect-video w-full rounded bg-black" />
                              <a
                                className="btn btn-sm w-full"
                                href={result.url}
                                download={`${result.profile.id}-${Math.round(result.actualBitrateMbps * 10) / 10}mbps.webm`}
                              >
                                Download
                              </a>
                            </div>
                          ) : (
                            <span className="text-text-dim">Pending</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <canvas ref={canvasRef} className="hidden" />
      </div>
    </main>
  );
}
